import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Linking, Pressable, ScrollView, Text, View } from 'react-native';
import { useDoc } from '../store/store';
import type { Hit, Oab, Profile } from '../store/types';
import { addProfile, updateProfile, removerPerfil, setHitStatus, giveConsent, limparMonitor, puxarCapturas, oabsDe, diasParaExcluir, atualizarHit } from '../store/monitor';
import { analisarComIA, analisarPorTexto, sugestoes, type Sugestao } from '../modules/monitor/analise';
import { configIA, iaPronta, useIAPronta } from '../lib/ia';
import { newTask, saveTask } from '../store/planner';
import { GRUPOS_TRIBUNAIS } from '../modules/monitor/tribunais';
import { verificar } from '../modules/monitor/check';
import { semBloqueio } from '../auth/AuthContext';
import { useTaskSheet } from '../components/TaskSheet';
import { useConfirmarIdentidade } from '../components/ConfirmarIdentidade';
import { Btn, Chip, Field, Screen, Sheet } from '../components/ui';
import { C, alpha, useCores } from '../components/theme';
import { brDateTime, brYmd, norm, today } from '../lib/date';
import { t } from '../lib/i18n';
import { useBotaoNovo } from '../lib/botaoNovo';
import { supabase } from '../lib/supabase';
import { ROLAGEM_TECLADO } from '../components/teclado';

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

/** Resumo do perfil: "OAB 13647/MS, 1234/PR · Todos os tribunais". */
function resumoPerfil(p: Profile) {
  const oabs = oabsDe(p);
  const partes: string[] = [];
  if (oabs.length) partes.push(`OAB ${oabs.map(o => `${o.numero}/${o.uf}`).join(', ')}`);
  const tr = p.tribunais ?? [];
  partes.push(!tr.length ? t('Todos os tribunais') : tr.length <= 4 ? tr.join(', ') : t('{n} tribunais', { n: tr.length }));
  if (p.diarios) partes.push(t('Diários municipais'));
  return partes.join(' · ');
}

/** "2026-10-02" ou "02/10/2026" → "02/10/2026" */
const dataBr = (s: string) => (/^\d{4}-\d{2}-\d{2}/.test(s) ? brYmd(s.slice(0, 10)) : s);
const poloNome = (p: string) => {
  const x = p.toUpperCase();
  if (x === 'A' || x.startsWith('ATIV')) return t('Polo ativo');
  if (x === 'P' || x.startsWith('PASS')) return t('Polo passivo');
  return p || t('Parte');
};

/** Cartão de uma publicação: origem, partes, trecho e ações (agenda, descartar, abrir). */
/** Sugestões de agendamento tiradas da publicação: o usuário aceita, edita ou recusa cada uma. */
function Sugestoes({ h, cor }: { h: Hit; cor: string }) {
  const sheet = useTaskSheet();
  const [lendo, setLendo] = useState(false);
  const temIA = useIAPronta();
  const a = h.ia;
  const lista = a ? sugestoes(h, a) : [];
  const feitas = h.sugFeitas ?? {};

  async function lerComIA() {
    setLendo(true);
    try { const r = await analisarComIA(h); atualizarHit(h.hash, h.profile_id, x => ({ ...x, ia: r, sugFeitas: {} })); }
    catch (e: any) { Alert.alert(t('Não foi possível'), String(e?.message ?? e)); }
    finally { setLendo(false); }
  }
  const marcar = (s: Sugestao, v: 'aceita' | 'recusada') => atualizarHit(h.hash, h.profile_id, x => ({ ...x, sugFeitas: { ...(x.sugFeitas ?? {}), [s.id]: v } }));
  function aceitar(s: Sugestao) {
    const tk = newTask(s.tarefa);
    saveTask(tk);
    marcar(s, 'aceita');
    setHitStatus(h.hash, h.profile_id, 'confirmed', tk.id);
  }
  const editar = (s: Sugestao) => sheet.open({ prefill: s.tarefa, onSaved: tk => { marcar(s, 'aceita'); setHitStatus(h.hash, h.profile_id, 'confirmed', tk.id); } });

  if (!a) return null;
  return (
    <View style={{ backgroundColor: alpha(cor, 0.1), borderRadius: 12, padding: 10, gap: 8, borderWidth: 1, borderColor: alpha(cor, 0.35) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ color: C.text, fontWeight: '800', flex: 1 }}>{a.fonte === 'ia' ? '🤖 ' + t('Leitura da IA') : '🔎 ' + t('Leitura automática do texto')}</Text>
        {temIA && <Pressable onPress={lerComIA} disabled={lendo} hitSlop={6}>
          {lendo ? <ActivityIndicator color={cor} size="small" /> : <Text style={{ color: cor, fontWeight: '700', fontSize: 12 }}>{a.fonte === 'ia' ? t('Ler de novo') : t('Analisar com IA')}</Text>}
        </Pressable>}
      </View>
      {!!a.ato && <Text style={{ color: C.text, fontWeight: '700' }}>{a.ato}</Text>}
      {!!a.resumo && <Text style={{ color: C.sub, fontSize: 13 }}>{a.resumo}</Text>}
      {!lista.length && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Nenhuma audiência ou prazo encontrado nesta publicação.')}</Text>}
      {lista.map(s => (
        <View key={s.id} style={{ backgroundColor: C.card, borderRadius: 10, padding: 10, gap: 6 }}>
          <Text style={{ color: C.text, fontWeight: '700' }}>{s.id === 'audiencia' ? '📅 ' : '⏳ '}{s.titulo}</Text>
          {!!s.detalhe && <Text style={{ color: C.sub, fontSize: 12 }}>{s.detalhe}</Text>}
          <Text style={{ color: C.mute, fontSize: 11 }} numberOfLines={2}>{s.tarefa.title}{s.tarefa.tz ? ` · 🌐 ${s.tarefa.tz}` : ''}</Text>
          {feitas[s.id] === 'aceita' ? <Text style={{ color: C.ok, fontWeight: '700', fontSize: 12 }}>✓ {t('Agendado')}</Text>
            : feitas[s.id] === 'recusada' ? (
              <Pressable onPress={() => atualizarHit(h.hash, h.profile_id, x => { const f = { ...(x.sugFeitas ?? {}) }; delete f[s.id]; return { ...x, sugFeitas: f }; })}>
                <Text style={{ color: C.mute, fontSize: 12 }}>{t('Recusada')} · <Text style={{ color: cor }}>{t('desfazer')}</Text></Text></Pressable>
            ) : (
              <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                <Pressable onPress={() => aceitar(s)} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 10, backgroundColor: cor }}>
                  <Text style={{ color: '#fff', fontWeight: '800', fontSize: 13 }}>✓ {t('Aceitar')}</Text></Pressable>
                <Pressable onPress={() => editar(s)} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 10, backgroundColor: C.card2 }}>
                  <Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>✏️ {t('Ajustar')}</Text></Pressable>
                <Pressable onPress={() => marcar(s, 'recusada')} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 10, backgroundColor: C.card2 }}>
                  <Text style={{ color: C.sub, fontWeight: '700', fontSize: 13 }}>{t('Recusar')}</Text></Pressable>
              </View>
            )}
        </View>
      ))}
      {a.fonte === 'texto' && !temIA && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Para uma leitura mais completa, ligue a sua IA em Ajustes › Inteligência artificial.')}</Text>}
    </View>
  );
}

function CartaoPublicacao({ h, cor, perfil, destaque, onAgenda }: { h: Hit; cor: string; perfil: string; destaque: boolean; onAgenda: (h: Hit) => void }) {
  const { tasks } = useDoc('planner');
  const [aberto, setAberto] = useState(destaque);
  const tarefa = h.taskId ? tasks.find(x => x.id === h.taskId) : undefined;
  const ativos = (h.partes ?? []).filter(p => poloNome(p.polo) === t('Polo ativo'));
  const passivos = (h.partes ?? []).filter(p => poloNome(p.polo) === t('Polo passivo'));
  const outros = (h.partes ?? []).filter(p => !ativos.includes(p) && !passivos.includes(p));

  return (
    <View style={{ backgroundColor: C.card, borderRadius: 18, padding: 14, gap: 8, borderWidth: destaque ? 2 : 1, borderColor: destaque ? cor : C.line }}>
      {/* origem */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {!!h.court && <View style={{ backgroundColor: alpha(cor, 0.2), paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 }}>
          <Text style={{ color: cor, fontWeight: '800', fontSize: 12 }}>{h.court}</Text></View>}
        {!!h.kind && <Text style={{ color: C.sub, fontSize: 12 }}>{h.kind}{h.tipoDoc && h.tipoDoc !== h.kind ? ` · ${h.tipoDoc}` : ''}</Text>}
        <Text style={{ color: C.mute, fontSize: 12, marginLeft: 'auto' }}>{dataBr(h.available)}</Text>
      </View>
      <Text style={{ color: C.text, fontWeight: '800', fontSize: 15 }} selectable>{h.process || t('Processo não informado')}</Text>
      {!!h.classe && <Text style={{ color: C.sub, fontSize: 12 }}>{h.classe}</Text>}
      {!!h.orgao && <Text style={{ color: C.sub, fontSize: 12 }}>🏛️ {h.orgao}</Text>}

      {/* partes */}
      {(ativos.length > 0 || passivos.length > 0 || outros.length > 0) && (
        <View style={{ backgroundColor: C.card2, borderRadius: 12, padding: 10, gap: 4 }}>
          {ativos.length > 0 && <Text style={{ color: C.text, fontSize: 13 }}><Text style={{ color: C.ok, fontWeight: '700' }}>{t('Polo ativo')}: </Text>{ativos.map(p => p.nome).join(', ')}</Text>}
          {passivos.length > 0 && <Text style={{ color: C.text, fontSize: 13 }}><Text style={{ color: C.warn, fontWeight: '700' }}>{t('Polo passivo')}: </Text>{passivos.map(p => p.nome).join(', ')}</Text>}
          {outros.length > 0 && <Text style={{ color: C.text, fontSize: 13 }}><Text style={{ color: C.sub, fontWeight: '700' }}>{t('Partes')}: </Text>{outros.map(p => p.nome).join(', ')}</Text>}
          {!!h.advogados?.length && <Text style={{ color: C.sub, fontSize: 12 }}>⚖️ {h.advogados.join(' · ')}</Text>}
        </View>
      )}

      {/* trecho */}
      <Pressable onPress={() => setAberto(a => !a)}>
        <Text numberOfLines={aberto ? undefined : 4} style={{ fontSize: 13, color: C.text, lineHeight: 19 }} selectable={aberto}>{h.text || t('Sem texto.')}</Text>
        {h.text.length > 220 && <Text style={{ color: cor, fontSize: 12, marginTop: 4, fontWeight: '700' }}>{aberto ? t('Mostrar menos') : t('Ler publicação inteira')}</Text>}
      </Pressable>
      {h.status !== 'dismissed' && <Sugestoes h={h} cor={cor} />}
      <Text style={{ fontSize: 11, color: C.mute }}>{t('Encontrada para {perfil}', { perfil })}{h.found_at ? ` · ${brDateTime(h.found_at)}` : ''}</Text>
      {h.status === 'dismissed' && (() => {
        const n = diasParaExcluir(h);
        return <Text style={{ fontSize: 12, color: C.warn, fontWeight: '700' }}>
          🗑️ {n === 0 ? t('Será excluída hoje') : n === 1 ? t('Será excluída em 1 dia') : t('Será excluída em {n} dias', { n })}</Text>;
      })()}

      {/* ações */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
        {h.status === 'confirmed' && tarefa
          ? <View style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: alpha(C.ok, 0.15) }}>
              <Text style={{ color: C.ok, fontWeight: '700', fontSize: 13 }}>📅 {t('Na agenda em {data}', { data: brYmd(tarefa.start) })}</Text></View>
          : <Pressable onPress={() => onAgenda(h)} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: cor }}>
              <Text style={{ color: '#fff', fontWeight: '800', fontSize: 13 }}>📅 {t('Colocar na agenda')}</Text></Pressable>}
        {h.status !== 'dismissed'
          ? <Pressable onPress={() => setHitStatus(h.hash, h.profile_id, 'dismissed')} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: C.card2 }}>
              <Text style={{ color: C.sub, fontWeight: '700', fontSize: 13 }}>{t('Descartar')}</Text></Pressable>
          : <Pressable onPress={() => setHitStatus(h.hash, h.profile_id, 'new')} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: C.card2 }}>
              <Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>↩ {t('Voltar para novas')}</Text></Pressable>}
        {h.link.startsWith('https://') && (
          <Pressable onPress={() => semBloqueio(() => Linking.openURL(h.link)).catch(() => {})} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: C.card2 }}>
            <Text style={{ color: '#60A5FA', fontWeight: '700', fontSize: 13 }}>{h.hash.startsWith('qd:') ? t('Abrir o diário oficial') : t('Abrir no DJEN')}</Text></Pressable>
        )}
      </View>
    </View>
  );
}

export default function Monitor({ item, onBack }: { item?: string; onBack: () => void }) {
  const m = useDoc('monitor');
  const { corDe } = useCores();
  const cor = corDe('monitor');
  const sheet = useTaskSheet();
  const ident = useConfirmarIdentidade(cor);   // perfil é item grande: remover pede a senha ou a biometria
  const [aceite, setAceite] = useState(false);
  const [st, setSt] = useState<Hit['status']>('new');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState(false);
  useBotaoNovo({ rotulo: t('Novo perfil'), fn: () => abrirForm() });   // 11d F: o + cria um perfil (antes não fazia nada aqui)
  const [editando, setEditando] = useState<string | null>(null);
  const [kind, setKind] = useState<'pessoa' | 'empresa'>('pessoa');
  const [label, setLabel] = useState('');
  const [name, setName] = useState('');
  const [oabs, setOabs] = useState<Oab[]>([{ numero: '', uf: '' }]);
  const [ufAberta, setUfAberta] = useState(0);
  const [todos, setTodos] = useState(true);
  const [tribs, setTribs] = useState<string[]>([]);
  const [diarios, setDiarios] = useState(false);
  const [decl, setDecl] = useState(false);
  const [busca, setBusca] = useState('');

  // Aberto a partir de uma tarefa: mostra a publicação vinculada.
  useEffect(() => {
    const h = item ? m.hits.find(x => x.hash === item) : undefined;
    if (h) setSt(h.status);
  }, [item]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leitura automática: o texto de toda publicação nova é lido na hora; com a IA ligada (e "analisar
  // sozinho" marcado), a IA lê até 10 por vez, uma de cada vez.
  const iaRodando = useRef(false);
  const iaOk = useIAPronta();
  useEffect(() => {
    const semLeitura = m.hits.filter(h => !h.ia && h.status === 'new');
    for (const h of semLeitura) atualizarHit(h.hash, h.profile_id, x => ({ ...x, ia: analisarPorTexto(x) }));
    (async () => {
      if (iaRodando.current || !configIA().auto || !(await iaPronta())) return;
      iaRodando.current = true;
      try {
        const fila = m.hits.filter(h => h.status === 'new' && h.ia?.fonte !== 'ia').slice(0, 10);
        for (const h of fila) {
          try { const r = await analisarComIA(h); atualizarHit(h.hash, h.profile_id, x => ({ ...x, ia: r })); }
          catch { break; }   // sem crédito ou sem internet: tenta na próxima vez
        }
      } finally { iaRodando.current = false; }
    })();
  }, [m.hits.length, iaOk]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ao abrir: limpa as descartadas vencidas e traz o que o servidor capturou.
  useEffect(() => {
    limparMonitor();
    puxarCapturas().then(n => { if (n) setMsg(t('{n} publicação(ões) nova(s).', { n })); }).catch(() => {});
  }, []);

  // auditoria 0.77: antes do "if (!m.consent)" (hook depois de um return derrubava a tela ao aceitar o aviso)
  // quando o servidor buscou pela última vez (para conferir se a captura automática está rodando)
  const [sit, setSit] = useState<{ texto: string; erro: boolean } | null>(null);
  useEffect(() => {
    supabase?.rpc('monitor_situacao').then(({ data }) => {
      const d: any = data; if (!d) return;
      const minha = d.minha_ultima_captura ? brDateTime(d.minha_ultima_captura) : null;
      const falhou = d.ultimo_status && d.ultimo_status !== 'succeeded';
      setSit({ erro: !!falhou || !minha, texto: (minha ? t('Última busca do servidor para você: {quando}', { quando: minha }) : t('O servidor ainda não buscou para os seus perfis.'))
        + (falhou ? ' · ' + t('a última execução falhou; avise o suporte') : '') });
    }, () => {});
  }, []);

  if (!m.consent) {
    return (
      <Screen title={t('Publicações')} onBack={onBack}>
        <View style={{ backgroundColor: C.card, borderRadius: 18, padding: 16, gap: 12 }}>
          <Text style={{ color: C.text }}>• {t('O app e o servidor do My Planner consultam o serviço público do CNJ (DJEN) com os nomes e as OABs que você cadastrar e, se você marcar, o Querido Diário (diários oficiais municipais). Esses dados são enviados a esses serviços a cada verificação.')}</Text>
          <Text style={{ color: C.text }}>• {t('Os perfis e os resultados ficam na sua conta do My Planner.')}</Text>
          <Text style={{ color: C.text }}>• {t('O servidor faz a captura automática de 2 em 2 horas, das 08h às 20h (horário de Brasília), mesmo com o app fechado. As publicações capturadas aparecem quando você abre o app. Também dá para usar "Verificar agora".')}</Text>
          <Text style={{ color: C.text }}>• {t('É um auxílio e não substitui o acompanhamento das intimações nos canais oficiais. Prazos devem ser conferidos no sistema oficial.')}</Text>
          <Pressable onPress={() => setAceite(!aceite)} style={{ flexDirection: 'row', gap: 10 }}>
            <Text style={{ fontSize: 20, color: C.text }}>{aceite ? '☑' : '☐'}</Text>
            <Text style={{ flex: 1, color: C.text }}>{t('Entendi e concordo em usar o monitor nessas condições.')}</Text>
          </Pressable>
          <Btn cor={cor} disabled={!aceite} label={t('Ativar monitor')} onPress={giveConsent} />
        </View>
      </Screen>
    );
  }

  async function checar() {
    setBusy(true); setMsg('');
    const capturadas = await puxarCapturas();
    const r = await verificar();
    const total = r.novos + capturadas;
    setMsg(r.erro ?? (total ? t('{n} publicação(ões) nova(s).', { n: total }) : t('Nenhuma publicação nova.')));
    setBusy(false);
  }
  function abrirForm(p?: Profile) {
    setEditando(p?.id ?? null);
    setKind(p?.kind ?? 'pessoa'); setLabel(p?.label ?? ''); setName(p?.name ?? '');
    const lista = p ? oabsDe(p) : [];
    setOabs(lista.length ? lista : [{ numero: '', uf: '' }]); setUfAberta(lista.length ? -1 : 0);
    setTodos(!p?.tribunais?.length); setTribs(p?.tribunais ?? []); setDiarios(!!p?.diarios);
    setDecl(p?.kind === 'empresa'); setForm(true);
  }
  function salvarPerfil() {
    if (!label.trim() || !name.trim()) return Alert.alert(t('Informe o rótulo e o nome.'));
    const preenchidas = kind === 'pessoa' ? oabs.map(o => ({ numero: o.numero.replace(/\D/g, ''), uf: o.uf })).filter(o => o.numero || o.uf) : [];
    if (preenchidas.some(o => !o.numero || !o.uf)) return Alert.alert(t('Para monitorar a OAB, informe o número e a UF.'));
    if (kind === 'empresa' && !decl) return Alert.alert(t('Confirme a declaração sobre a empresa.'));
    if (!todos && !tribs.length) return Alert.alert(t('Escolha ao menos um tribunal ou marque "Todos os tribunais".'));
    const dados = { kind, label, name, oabs: preenchidas, tribunais: todos ? [] : tribs, diarios };
    if (editando) updateProfile(editando, dados); else addProfile(dados);
    setForm(false); setEditando(null);
  }
  const mudarOab = (i: number, o: Partial<Oab>) => setOabs(l => l.map((x, j) => (j === i ? { ...x, ...o } : x)));
  const alternarTrib = (s: string) => setTribs(l => (l.includes(s) ? l.filter(x => x !== s) : [...l, s]));

  /** Abre a ficha da tarefa já preenchida com a publicação; ao salvar, a publicação vai para "Na agenda". */
  function colocarNaAgenda(h: Hit) {
    const partes = (h.partes ?? []).map(p => `${poloNome(p.polo)}: ${p.nome}`).join('\n');
    const notas = [
      `${t('Processo')}: ${h.process || '-'}`,
      [h.court, h.orgao].filter(Boolean).join(' · '),
      h.classe,
      `${t('Disponibilizada em')}: ${dataBr(h.available)}`,
      partes,
      h.advogados?.length ? `${t('Advogados')}: ${h.advogados.join(', ')}` : '',
      h.link.startsWith('https://') ? h.link : '',
      '',
      t('Confira o prazo no sistema oficial.'),
      '',
      h.text.slice(0, 1500),
    ].filter((x, i, a) => x !== '' || a[i - 1] !== '').join('\n');
    sheet.open({
      prefill: {
        title: `${t('Intimação')}: ${h.process || h.court || t('publicação')}`, area: 'monitor', energy: 3, emoji: '⚖️', emojiManual: true,
        start: today(), notes: notas, alerts: { mode: 'before', before: [60, 1440] },
        link: { aba: 'monitor', id: h.hash, label: h.process || h.court || t('Publicação') },
        subtasks: [{ id: `${h.hash}-1`, text: t('Ler a publicação inteira') }, { id: `${h.hash}-2`, text: t('Conferir o prazo no sistema oficial') }],
      },
      onSaved: tk => setHitStatus(h.hash, h.profile_id, 'confirmed', tk.id),
    });
  }

  const contagem = (s: Hit['status']) => m.hits.filter(h => h.status === s).length;
  const ABAS: [Hit['status'], string][] = [['new', t('Novas')], ['confirmed', t('Na agenda')], ['dismissed', t('Descartadas')]];
  const termos = norm(busca).split(/\s+/).filter(Boolean);
  const textoDe = (h: Hit) => norm([h.process, h.court, h.orgao, h.classe, h.kind, h.tipoDoc, h.text, h.available, brYmd(h.available.slice(0, 10)),
    ...(h.partes ?? []).map(p => p.nome), ...(h.advogados ?? []), h.ia?.ato, h.ia?.resumo].filter(Boolean).join(' '));
  const hits = m.hits.filter(h => (termos.length ? termos.every(x => textoDe(h).includes(x)) : h.status === st))
    .sort((a, b) => (a.hash === item ? -1 : b.hash === item ? 1 : 0) || b.available.localeCompare(a.available)).slice(0, 600);
  const nomeDo = (pid: string) => m.profiles.find(p => p.id === pid)?.label ?? '';


  return (
    <Screen title={t('Publicações')} onBack={onBack}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8 }}>
        {m.profiles.map(p => (
          <Pressable key={p.id} style={{ backgroundColor: alpha(cor, 0.14), padding: 10, borderRadius: 14, maxWidth: 260 }}
            onPress={() => abrirForm(p)}
            delayLongPress={1000} onLongPress={() => Alert.alert(t('Remover "{nome}"?', { nome: p.label }), [t('Os resultados deste perfil também serão apagados.'), t('Fica 30 dias na lixeira.')].join('\n\n'), [
              { text: t('Cancelar'), style: 'cancel' }, { text: t('Remover'), style: 'destructive', onPress: () => ident.pedir(t('Confirme para excluir')).then(ok => ok && removerPerfil(p.id)) }])}>
            <Text style={{ color: C.text, fontWeight: '700' }}>{p.kind === 'empresa' ? '🏢' : '👤'} {p.label}</Text>
            <Text numberOfLines={1} style={{ fontSize: 12, color: C.sub }}>{p.name}</Text>
            <Text numberOfLines={2} style={{ fontSize: 12, color: C.sub }}>{resumoPerfil(p)}</Text>
            <Text style={{ fontSize: 11, color: C.mute }}>{p.last_check ? t('verificado {quando}', { quando: brDateTime(p.last_check) }) : t('nunca verificado')}</Text>
          </Pressable>
        ))}
        <Chip cor={cor} on={false} label={t('+ Perfil')} onPress={() => abrirForm()} />
      </ScrollView>
      {m.profiles.length > 0 && <Text style={{ fontSize: 11, color: C.mute }}>{t('Toque num perfil para editar. Pressione e segure para removê-lo.')}</Text>}
      {m.profiles.length > 5 && <Text style={{ fontSize: 11, color: C.mute }}>{t('A busca automática do servidor acompanha os 5 primeiros perfis. Os outros entram quando você toca em "Verificar agora".')}</Text>}
      <Text style={{ fontSize: 12, color: C.sub }}>⏰ {t('Captura automática do servidor: de 2 em 2 horas, das 08h às 20h')}</Text>
      {!!sit && <Text style={{ fontSize: 11, color: sit.erro ? C.warn : C.mute }}>{sit.texto}</Text>}
      <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
        <Btn cor={cor} label={t('Verificar agora')} onPress={checar} disabled={busy || !m.profiles.length} />
        {busy && <ActivityIndicator color={cor} />}
        <Text style={{ flex: 1, fontSize: 12, color: C.sub }}>{msg}</Text>
      </View>
      <Field value={busca} onChangeText={setBusca} placeholder={'🔍 ' + t('Buscar: processo, parte, tribunal, palavra...')} autoCorrect={false} />
      {!!termos.length && <Text style={{ color: C.sub, fontSize: 12 }}>{t('{n} resultado(s) em todas as publicações', { n: hits.length })}</Text>}
      {!termos.length && <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        {ABAS.map(([k, l]) => <Chip key={k} cor={cor} on={st === k} label={`${l} (${contagem(k)})`} onPress={() => setSt(k)} />)}
      </View>}
      {st === 'dismissed' && <Text style={{ fontSize: 12, color: C.sub }}>{t('Excluídas em definitivo 30 dias depois do descarte.')}</Text>}
      {!hits.length && <Text style={{ color: C.sub }}>{m.profiles.length ? t('Nada por aqui.') : t('Crie um perfil com nome e, se for advogado, a OAB.')}</Text>}
      {hits.map(h => (
        <CartaoPublicacao key={`${h.hash}:${h.profile_id}`} h={h} cor={cor} perfil={nomeDo(h.profile_id)} destaque={h.hash === item} onAgenda={colocarNaAgenda} />
      ))}

      <Sheet visible={form} onClose={() => setForm(false)} title={editando ? t('Editar perfil') : t('Novo perfil')}
        right={<Pressable onPress={salvarPerfil}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
        <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Chip cor={cor} on={kind === 'pessoa'} label={t('Pessoa')} onPress={() => setKind('pessoa')} />
            <Chip cor={cor} on={kind === 'empresa'} label={t('Empresa')} onPress={() => setKind('empresa')} />
          </View>
          <Field value={label} onChangeText={setLabel} placeholder={t('Rótulo (ex.: Meu nome, Empresa do cliente)')} />
          <Field value={name} onChangeText={setName} placeholder={kind === 'pessoa' ? t('Nome completo') : t('Razão social ou nome fantasia')} />
          {kind === 'pessoa' && (<>
            <Text style={{ color: C.sub }}>{t('OAB (opcional, para advogados)')}</Text>
            {oabs.map((o, i) => (
              <View key={i} style={{ backgroundColor: C.card, borderRadius: 14, padding: 10, gap: 8 }}>
                <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                  <Field style={{ flex: 1 }} value={o.numero} onChangeText={v => mudarOab(i, { numero: v })} keyboardType="number-pad" placeholder={t('Número da OAB')} />
                  <Pressable onPress={() => setUfAberta(ufAberta === i ? -1 : i)} style={{ paddingHorizontal: 14, paddingVertical: 12, borderRadius: 12, backgroundColor: C.card2 }}>
                    <Text style={{ color: o.uf ? C.text : C.mute, fontWeight: '700' }}>{o.uf || t('UF')} ▾</Text></Pressable>
                  {oabs.length > 1 && <Pressable hitSlop={8} onPress={() => { setOabs(l => l.filter((_, j) => j !== i)); setUfAberta(-1); }}>
                    <Text style={{ color: C.sub, fontSize: 18 }}>✕</Text></Pressable>}
                </View>
                {ufAberta === i && <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {UFS.map(u => <Chip key={u} cor={cor} small on={o.uf === u} label={u} onPress={() => { mudarOab(i, { uf: o.uf === u ? '' : u }); setUfAberta(-1); }} />)}
                </View>}
              </View>
            ))}
            <Pressable onPress={() => { setOabs(l => [...l, { numero: '', uf: '' }]); setUfAberta(oabs.length); }}>
              <Text style={{ color: cor, fontWeight: '700' }}>{t('+ Adicionar outra OAB')}</Text></Pressable>
            <Text style={{ fontSize: 12, color: C.warn }}>{t('Cadastre apenas o seu próprio nome e a sua OAB.')}</Text>
          </>)}
          <Text style={{ color: C.sub, marginTop: 6 }}>{t('Tribunais')}</Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Chip cor={cor} on={todos} label={t('Todos os tribunais')} onPress={() => setTodos(true)} />
            <Chip cor={cor} on={!todos} label={t('Escolher tribunais')} onPress={() => setTodos(false)} />
          </View>
          {!todos && (
            <View style={{ backgroundColor: C.card, borderRadius: 14, borderWidth: 1, borderColor: C.line }}>
              <Text style={{ color: C.sub, fontSize: 12, padding: 10, paddingBottom: 0 }}>
                {tribs.length ? t('{n} selecionado(s): {lista}', { n: tribs.length, lista: tribs.join(', ') }) : t('Nenhum selecionado')}</Text>
              <ScrollView style={{ maxHeight: 340 }} nestedScrollEnabled contentContainerStyle={{ padding: 10, gap: 6 }}>
                {GRUPOS_TRIBUNAIS.map(g => {
                  const todosDoGrupo = g.siglas.every(x => tribs.includes(x));
                  return (
                    <View key={g.nome} style={{ gap: 6, marginBottom: 6 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Text style={{ flex: 1, color: C.text, fontWeight: '700' }}>{t(g.nome)}</Text>
                        <Pressable hitSlop={6} onPress={() => setTribs(l => (todosDoGrupo ? l.filter(x => !g.siglas.includes(x)) : [...new Set([...l, ...g.siglas])]))}>
                          <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>{todosDoGrupo ? t('Desmarcar grupo') : t('Marcar grupo')}</Text></Pressable>
                      </View>
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                        {g.siglas.map(x => <Chip key={x} cor={cor} small on={tribs.includes(x)} label={x} onPress={() => alternarTrib(x)} />)}
                      </View>
                    </View>
                  );
                })}
              </ScrollView>
            </View>
          )}
          <Pressable onPress={() => setDiarios(!diarios)} style={{ flexDirection: 'row', gap: 10, marginTop: 6 }}>
            <Text style={{ fontSize: 20, color: C.text }}>{diarios ? '☑' : '☐'}</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text }}>{t('Também procurar nos Diários Oficiais dos municípios')}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>{t('Busca o nome nos diários municipais reunidos pelo Querido Diário (Open Knowledge Brasil). Nem todos os municípios estão incluídos, e os diários oficiais não fazem parte do DJEN.')}</Text>
            </View>
          </Pressable>
          {kind === 'empresa' && (
            <Pressable onPress={() => setDecl(!decl)} style={{ flexDirection: 'row', gap: 10 }}>
              <Text style={{ fontSize: 20, color: C.text }}>{decl ? '☑' : '☐'}</Text>
              <Text style={{ flex: 1, color: C.text }}>{t('Declaro que esta empresa é minha ou de cliente que autorizou o acompanhamento.')}</Text>
            </Pressable>
          )}
          <Btn cor={cor} label={t('Salvar')} onPress={salvarPerfil} />
        </ScrollView>
      </Sheet>
      {ident.tela}
    </Screen>
  );
}
